// The day close as an end-of-day report (spec/13 §10, F-OPS-06): what the manager reads before closing the day, built
// from the Reports screen's kit (../reports/report-kit and the @clavian/ui-web charts) so the two are one visual
// system — the same headline figures, card headers, equal-height grid and charts.
//
//   Headline figures      Money in (every tender), Revenue (M-01), the cash variance, and — for a member who may read
//                         the venue's reports — sessions and occupancy (else what was paid from wallets).
//   Money taken           a donut of money in by tender — cash, UPI, card, and online as the rest — so its total *is*
//                         money in; what was paid from wallets sits under it, as revenue that is not money taken.
//   From sales to revenue a waterfall from the day's sales, term by term (`revenueBridge`, exact from the ledger:
//                         discounts, tax, refunds, on account, wallet bonus), to the revenue.
//   Sales by category     ranked bars; a category that sold nothing is a quiet chip.
//   Adjustments           voids, refunds, discounts, comps as tiles; the ones that did not happen as chips.
//   Beside revenue        wallet top-ups, receivables, wallet credit given, and pass sales (already revenue).
//   Shifts of the day     each shift with its cashier, count and variance.
//
// `DayCloseSection` composes the report with the controls (./DayCloseControls), which stay apart so the business-day
// lifecycle can change without touching the report. The server owns every figure; this only lays them out.

import type { DayCloseReview, DayCloseShift, DayRevenueBridge } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, BarChart, Button, ChartEmpty, type ChartTone, ChartZeros, DonutChart, EmptyState, Skeleton, StatCard, Table, type TableColumn, WaterfallChart } from '@clavian/ui-web';
import { useId, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { HeadlineMoney, headlineMoney, KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage, ReportRow, ReportRows, ReportTiles, Signed } from '../reports/report-kit';
import { useVenueReportOverview } from '../reports/queries';
import { useDayCloseControls } from './DayCloseControls';
import { Variance, varianceTone } from './variance';

import './day-close.css';

type Bridge = NonNullable<DayRevenueBridge>;

/** What the headline row reads from the Reports overview for the day (`reports:financial`). */
export interface DaySessions {
  readonly sessions: { readonly sessions: number } | null;
  readonly occupancy: { readonly hoursSet: boolean; readonly total: { readonly ratio: { readonly basisPoints: number } | null } } | null;
}

export function DayCloseSection({
  review,
  error,
  refetch,
  organizationId,
  venueId,
  onOpenShift,
  canReadReports,
}: {
  readonly review: DayCloseReview | undefined;
  readonly error: unknown;
  readonly refetch: () => void;
  readonly organizationId: string;
  readonly venueId: string;
  readonly onOpenShift: ((shiftId: string) => void) | undefined;
  /** `reports:financial`: the day's sessions and occupancy come from the Reports overview, which needs it. */
  readonly canReadReports: boolean;
}): ReactNode {
  const headingId = useId();
  const day = review?.businessDay ?? '';
  const report = useVenueReportOverview(organizationId, venueId, day, day, canReadReports && review !== undefined);
  return (
    <section className="console-dayclose" aria-labelledby={headingId}>
      {review === undefined ? (
        <>
          <DayCloseHead id={headingId} />
          {error !== undefined && error !== null ? <LoadFailure error={error} onRetry={refetch} /> : <Skeleton shape="text" lines={3} />}
        </>
      ) : (
        <Loaded review={review} headingId={headingId} organizationId={organizationId} venueId={venueId} onOpenShift={onOpenShift} sessions={canReadReports ? report.data : undefined} />
      )}
    </section>
  );
}

function DayCloseHead({ id, date, action }: { readonly id: string; readonly date?: string; readonly action?: ReactNode }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <header className="console-dayclose__head">
      <div>
        <h2 id={id} className="console-dayclose__title">
          {t('shifts.dayClose')}
        </h2>
        {date !== undefined ? <p className="console-dayclose__date">{date}</p> : null}
      </div>
      {action}
    </header>
  );
}

function Loaded({
  review,
  headingId,
  organizationId,
  venueId,
  onOpenShift,
  sessions,
}: {
  readonly review: DayCloseReview;
  readonly headingId: string;
  readonly organizationId: string;
  readonly venueId: string;
  readonly onOpenShift: ((shiftId: string) => void) | undefined;
  readonly sessions: DaySessions | undefined;
}): ReactNode {
  const format = useVenueFormatter();
  const controls = useDayCloseControls({ review, organizationId, venueId });
  return (
    <>
      <DayCloseHead id={headingId} date={format.businessDate(review.businessDay)} action={controls.action} />
      {controls.notices}
      <DayCloseReport review={review} sessions={sessions} onOpenShift={onOpenShift} />
      {controls.dialog}
    </>
  );
}

/** The report itself: presentation only, from the review (and the day's sessions when the member may read them). */
export function DayCloseReport({
  review,
  sessions,
  onOpenShift,
}: {
  readonly review: DayCloseReview;
  readonly sessions: DaySessions | undefined;
  readonly onOpenShift: ((shiftId: string) => void) | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <ReportPage>
      <Kpis review={review} sessions={sessions} />
      <ReportGrid layout="even">
        <MoneyTakenCard review={review} />
        <BridgeCard review={review} />
      </ReportGrid>
      <ReportGrid layout="three">
        <CategoriesCard review={review} />
        <AdjustmentsCard review={review} />
        <NotRevenueCard review={review} />
      </ReportGrid>
      <ReportCard title={t('shifts.review.shifts')} description={t('shifts.review.shiftsHint')} flush>
        {review.shifts.length === 0 ? (
          <EmptyState size="compact" title={t('shifts.review.noShifts')} description={t('shifts.review.noShiftsHint')} />
        ) : (
          <DayShiftsTable shifts={review.shifts} onOpen={onOpenShift} />
        )}
      </ReportCard>
    </ReportPage>
  );
}

// -------------------------------------------------------------------------------------------
// The headline figures
// -------------------------------------------------------------------------------------------

function Kpis({ review, sessions }: { readonly review: DayCloseReview; readonly sessions: DaySessions | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  // Money in is the server's (`revenueBreakdownOf`): cash, card, UPI and online taken, top-ups included. Not the tenders'
  // sum — the wallet tender's money came in when the wallet was topped up, so adding it would count it twice.
  const moneyIn = review.moneyIn;
  const closedShifts = review.shifts.filter((row) => row.status === 'closed').length;
  const tone = varianceTone(review.totalCashVariance);
  const occupancy = sessions?.occupancy?.total.ratio?.basisPoints;
  return (
    <ReportKpis label={t('shifts.dayClose')}>
      <StatCard label={t('shifts.review.kpi.moneyIn')} value={<HeadlineMoney format={format} value={moneyIn} />} hint={t('shifts.review.kpi.moneyInHint')} />
      <StatCard
        label={t('shifts.review.revenue')}
        value={review.revenue === null ? '—' : <HeadlineMoney format={format} value={review.revenue} />}
        hint={
          review.revenue === null
            ? t('shifts.review.kpi.noRevenue')
            : review.walletSpent.amount !== 0
              ? t('shifts.review.kpi.revenueWalletHint', { wallet: format.money(review.walletSpent) })
              : t('shifts.review.kpi.revenueHint')
        }
      />
      <StatCard
        label={t('shifts.review.kpi.variance')}
        value={<HeadlineMoney format={format} value={review.totalCashVariance} />}
        unit={t(`shifts.varianceWord.${tone}`)}
        tone={tone === 'short' ? 'danger' : tone === 'over' ? 'warn' : 'default'}
        hint={closedShifts === 0 ? t('shifts.review.kpi.varianceNone') : t('shifts.review.kpi.varianceHint', { count: closedShifts })}
      />
      {sessions?.sessions !== undefined && sessions.sessions !== null ? (
        <StatCard
          label={t('shifts.review.kpi.sessions')}
          value={<KpiFigure value={sessions.sessions.sessions} format={(n) => format.number(n)} />}
          hint={
            occupancy === undefined || sessions.occupancy?.hoursSet !== true
              ? t('shifts.review.kpi.sessionsNoOccupancy')
              : t('shifts.review.kpi.sessionsHint', { occupancy: format.number(occupancy / 10_000, { style: 'percent', maximumFractionDigits: 0 }) })
          }
        />
      ) : (
        <StatCard
          label={t('shifts.review.kpi.walletSpent')}
          value={<HeadlineMoney format={format} value={review.walletSpent} />}
          hint={t('shifts.review.kpi.walletSpentHint')}
        />
      )}
    </ReportKpis>
  );
}

// -------------------------------------------------------------------------------------------
// Money taken (= money in), and what was paid from wallets beside it
// -------------------------------------------------------------------------------------------

/** The drawer's tenders, in the chart kit's order; online (and anything else money in counts) is the remainder. */
const TAKEN_ORDER: readonly { readonly tender: 'cash' | 'counter_upi' | 'counter_card'; readonly tone: ChartTone }[] = [
  { tender: 'cash', tone: 1 },
  { tender: 'counter_upi', tone: 2 },
  { tender: 'counter_card', tone: 3 },
];

/**
 * Money taken today, by tender — exactly money in: cash, UPI and card from the tills, and online (with anything else
 * money in counts) as the rest, so the donut's total is the headline figure. Wallet payments are not money taken (it
 * came in when the wallet was topped up): they sit under it, as revenue.
 */
function MoneyTakenCard({ review }: { readonly review: DayCloseReview }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const currency = review.moneyIn.currency;
  const amountOf = (tender: string) => review.tenders.find((row) => row.tender === tender)?.amount.amount ?? 0;
  const counter = TAKEN_ORDER.map((entry) => ({ ...entry, amount: amountOf(entry.tender) }));
  const rest = review.moneyIn.amount - counter.reduce((sum, entry) => sum + entry.amount, 0);
  const slices = [
    ...counter.filter((entry) => entry.amount !== 0).map((entry) => ({ key: entry.tender, label: t(`shifts.tender.${entry.tender}`), value: entry.amount, display: format.money({ amount: entry.amount, currency }), tone: entry.tone })),
    ...(rest !== 0 ? [{ key: 'online', label: t('shifts.review.taken.online'), value: rest, display: format.money({ amount: rest, currency }), tone: 4 as const, hint: t('shifts.review.taken.onlineHint') }] : []),
  ];
  const unused = counter.filter((entry) => entry.amount === 0);
  return (
    <ReportCard title={t('shifts.review.taken.title')} description={t('shifts.review.taken.hint')}>
      {slices.length === 0 ? (
        <ChartEmpty>{t('shifts.review.noTakings')}</ChartEmpty>
      ) : (
        <DonutChart
          label={t('shifts.review.taken.title')}
          slices={slices}
          total={{ display: headlineMoney(format, review.moneyIn), label: t('shifts.review.kpi.moneyIn') }}
          percent={(share) => format.number(share, { style: 'percent', maximumFractionDigits: 0 })}
        />
      )}
      <ChartZeros label={t('shifts.review.unusedLabel')} items={slices.length === 0 ? [] : unused.map((entry) => ({ key: entry.tender, label: t(`shifts.tender.${entry.tender}`) }))} />
      <ReportRows>
        <ReportRow strong label={t('shifts.review.taken.total')} value={format.money(review.moneyIn)} />
        <ReportRow label={t('shifts.review.taken.wallets')} hint={t('shifts.review.taken.walletsHint')} value={format.money(review.walletSpent)} quiet={review.walletSpent.amount === 0} />
      </ReportRows>
    </ReportCard>
  );
}

// -------------------------------------------------------------------------------------------
// From sales to revenue: a waterfall
// -------------------------------------------------------------------------------------------

/** One step of the reconciliation and whether it usually adds to the running figure (tax) or takes from it. */
const BRIDGE_STEPS: readonly { readonly key: Exclude<keyof Bridge, 'sales' | 'revenue'>; readonly adds: boolean }[] = [
  { key: 'discounts', adds: false },
  { key: 'tax', adds: true },
  { key: 'taxPaidAtTopUp', adds: true },
  { key: 'refunds', adds: false },
  { key: 'onAccount', adds: false },
  { key: 'walletBonus', adds: false },
  { key: 'walletSettling', adds: false },
  { key: 'other', adds: false },
];

/**
 * Sales by category → revenue as a waterfall: sales stand on zero, each term steps from the running figure, revenue
 * lands where they end. A step that did not happen is left out, so the chart stays short; the server's terms sum
 * exactly (`dayRevenueBridgeOf`).
 */
function BridgeCard({ review }: { readonly review: DayCloseReview }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const bridge = review.revenueBridge;
  return (
    <ReportCard title={t('shifts.review.bridge.title')} description={t('shifts.review.bridge.hint')}>
      {bridge === null ? (
        <ChartEmpty>{t('shifts.review.bridge.noRevenue')}</ChartEmpty>
      ) : (
        <WaterfallChart
          label={t('shifts.review.bridge.title')}
          steps={[
            { key: 'sales', label: t('shifts.review.bridge.sales'), value: bridge.sales.amount, total: true, display: format.money(bridge.sales) },
            ...BRIDGE_STEPS.filter((step) => bridge[step.key].amount !== 0).map((step) => {
              const value = bridge[step.key];
              // A term against its usual direction reads as its opposite: an older tab paid today adds to revenue.
              const delta = step.adds ? value.amount : -value.amount;
              const label = step.key === 'onAccount' && value.amount < 0 ? t('shifts.review.bridge.onAccountSettled') : t(`shifts.review.bridge.${step.key}`);
              return { key: step.key, label, value: delta, display: <Signed sign={delta >= 0 ? '+' : '−'} amount={format.money({ amount: Math.abs(value.amount), currency: value.currency })} /> };
            }),
            { key: 'revenue', label: t('shifts.review.bridge.revenue'), value: bridge.revenue.amount, total: true, display: format.money(bridge.revenue) },
          ]}
        />
      )}
      {bridge !== null && review.walletSpent.amount !== 0 ? <ReportNote>{t('shifts.review.bridge.walletSpent', { amount: format.money(review.walletSpent) })}</ReportNote> : null}
      {review.passSales.amount !== 0 ? <ReportNote>{t('shifts.review.bridge.passes', { amount: format.money(review.passSales) })}</ReportNote> : null}
    </ReportCard>
  );
}

// -------------------------------------------------------------------------------------------
// Sales by category
// -------------------------------------------------------------------------------------------

function CategoriesCard({ review }: { readonly review: DayCloseReview }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const sold = review.categories.filter((row) => row.amount.amount > 0).sort((a, b) => b.amount.amount - a.amount.amount);
  const total = sold.reduce((sum, row) => sum + row.amount.amount, 0);
  const nothing = review.categories.filter((row) => row.amount.amount <= 0);
  return (
    <ReportCard title={t('shifts.review.categories')} description={t('shifts.review.categoriesHint')}>
      {sold.length === 0 ? (
        <ChartEmpty>{t('shifts.review.noSales')}</ChartEmpty>
      ) : (
        <BarChart
          label={t('shifts.review.categories')}
          rows={sold.map((row) => ({
            key: row.category,
            label: t(`shifts.category.${row.category}`),
            value: row.amount.amount,
            display: format.money(row.amount),
            meta: format.number(total === 0 ? 0 : row.amount.amount / total, { style: 'percent', maximumFractionDigits: 0 }),
          }))}
        />
      )}
      <ChartZeros label={t('shifts.review.notSold')} items={sold.length === 0 ? [] : nothing.map((row) => ({ key: row.category, label: t(`shifts.category.${row.category}`) }))} />
    </ReportCard>
  );
}

// -------------------------------------------------------------------------------------------
// Adjustments
// -------------------------------------------------------------------------------------------

function AdjustmentsCard({ review }: { readonly review: DayCloseReview }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const items: readonly { readonly key: string; readonly label: string; readonly value: string; readonly hint?: string; readonly happened: boolean }[] = [
    { key: 'voids', label: t('shifts.review.voids'), value: format.money(review.voids.amount), hint: t('shifts.review.timesCount', { count: review.voids.count }), happened: review.voids.count > 0 },
    {
      key: 'refunds',
      label: t('shifts.review.refunds'),
      value: format.money(review.refunds.amount),
      hint: t('shifts.review.timesCount', { count: review.refunds.count }),
      happened: review.refunds.count > 0 || review.refunds.amount.amount !== 0,
    },
    { key: 'discounts', label: t('shifts.review.discounts'), value: format.money(review.discounts), happened: review.discounts.amount !== 0 },
    {
      key: 'comps',
      label: t('shifts.review.comps'),
      value: t('shifts.review.compsMinutes', { minutes: format.number(Math.round(review.comps.seconds / 60)) }),
      hint: t('shifts.review.compsSessions', { count: review.comps.sessions }),
      happened: review.comps.sessions > 0,
    },
  ];
  const happened = items.filter((item) => item.happened);
  const none = items.filter((item) => !item.happened);
  return (
    <ReportCard title={t('shifts.review.corrections')} description={t('shifts.review.correctionsHint')}>
      {happened.length > 0 ? (
        <ReportTiles label={t('shifts.review.corrections')} items={happened.map((item) => ({ key: item.key, label: item.label, value: item.value, ...(item.hint === undefined ? {} : { hint: item.hint }) }))} />
      ) : (
        <ChartEmpty>{t('shifts.review.noAdjustments')}</ChartEmpty>
      )}
      <ChartZeros label={t('shifts.review.noneToday')} items={happened.length === 0 ? [] : none.map((item) => ({ key: item.key, label: item.label }))} />
    </ReportCard>
  );
}

// -------------------------------------------------------------------------------------------
// Money that isn't revenue yet
// -------------------------------------------------------------------------------------------

function NotRevenueCard({ review }: { readonly review: DayCloseReview }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return (
    <ReportCard title={t('shifts.review.money')} description={t('shifts.review.moneyHint')}>
      <ReportRows>
        <ReportRow label={t('shifts.review.walletTopUps')} hint={t('shifts.review.notRevenue.walletTopUps')} value={format.money(review.walletTopUps)} quiet={review.walletTopUps.amount === 0} />
        <ReportRow label={t('shifts.review.receivables')} hint={t('shifts.review.notRevenue.receivables')} value={format.money(review.receivablesRaised)} quiet={review.receivablesRaised.amount === 0} />
        <ReportRow
          label={t('shifts.review.notRevenue.walletCredit')}
          hint={t('shifts.review.notRevenue.walletCreditHint')}
          value={format.money(review.walletCreditGiven)}
          quiet={review.walletCreditGiven.amount === 0}
        />
        <ReportRow label={t('shifts.review.passSales')} hint={t('shifts.review.notRevenue.passSales')} value={format.money(review.passSales)} quiet={review.passSales.amount === 0} />
      </ReportRows>
    </ReportCard>
  );
}

// -------------------------------------------------------------------------------------------
// Shifts of the day
// -------------------------------------------------------------------------------------------

/** The day's shifts on the review: counter, cashier, count and variance, the alerted ones marked (spec/13 §10). */
function DayShiftsTable({ shifts, onOpen }: { readonly shifts: readonly DayCloseShift[]; readonly onOpen: ((shiftId: string) => void) | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const columns: readonly TableColumn<DayCloseShift>[] = [
    {
      key: 'counter',
      header: t('shifts.counter'),
      cell: (shift) =>
        onOpen === undefined ? (
          <span className="console-shift-counter">{shift.counterName ?? t('shifts.noCounter')}</span>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => onOpen(shift.id)}>
            {shift.counterName ?? t('shifts.noCounter')}
          </Button>
        ),
    },
    { key: 'cashier', header: t('shifts.review.cashier'), cell: (shift) => shift.openedByName ?? <span className="console-subtle">—</span> },
    {
      key: 'status',
      header: t('shifts.statusLabel'),
      cell: (shift) => (
        <Badge tone={shift.status === 'open' ? 'warn' : 'neutral'} dot>
          {t(`shifts.status.${shift.status}`)}
        </Badge>
      ),
    },
    { key: 'counted', header: t('shifts.counted'), numeric: true, cell: (shift) => <span className="console-money">{shift.countedCash === null ? '—' : format.money(shift.countedCash)}</span> },
    {
      key: 'variance',
      header: t('shifts.variance'),
      numeric: true,
      cell: (shift) =>
        shift.cashVariance === null ? (
          <span className="console-subtle">—</span>
        ) : (
          <span className="console-dayclose-variance-cell">
            <Variance value={shift.cashVariance} />
            {shift.varianceAlerted ? (
              <Badge tone="danger" dot>
                {t('shifts.review.alerted')}
              </Badge>
            ) : null}
          </span>
        ),
    },
  ];
  // An open drawer carries the live rail, an alerted variance the danger rail (ADR-0043).
  return (
    <Table
      caption={t('shifts.review.shifts')}
      captionHidden
      columns={columns}
      rows={shifts}
      rowKey={(shift) => shift.id}
      rowTone={(shift) => (shift.varianceAlerted ? 'danger' : shift.status === 'open' ? 'live' : undefined)}
      empty={t('shifts.review.noShifts')}
    />
  );
}
